import React from "react";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import {
  Users,
  Tag,
  Receipt,
  FileText,
  AlertTriangle,
  ShieldCheck,
  Box,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { useParams } from "react-router-dom";
import { useGetPlanDetail } from "../../../hooks";
import { BadgeList } from "@shared/components/BadgeList";

const OverviewTab = () => {
  const { id } = useParams();
  const { data: planDetailResponse } = useGetPlanDetail(id);
  const planDetail = planDetailResponse?.result;

  const renderEligibilityTags = (
    items: any[],
    type: "Categories" | "Sub-Categories",
  ) => {
    const isCategory = type === "Categories";
    const bgClass = isCategory
      ? "bg-purple-50 text-purple-700 border-purple-200"
      : "bg-gray-50 text-gray-700 border-gray-200";

    const itemNames = Array.isArray(items)
      ? items.map((item: any) =>
          typeof item === "string"
            ? item
            : item?.name || item?.value || item?.code || String(item),
        )
      : [];

    return (
      <div>
        <div className="text-xs font-semibold text-gray-500 mb-2 tracking-wide">
          {type}
        </div>
        <BadgeList
          items={itemNames}
          count={2}
          badgeClassName={bgClass}
          fallback={
            <span className="text-xs text-gray-400">None specified</span>
          }
        />
      </div>
    );
  };

  const renderContractTags = (contracts: any[]) => {
    const itemNames = Array.isArray(contracts)
      ? contracts.map((contract: any) =>
          typeof contract === "string"
            ? contract
            : contract?.name ||
              contract?.value ||
              contract?.code ||
              String(contract),
        )
      : [];

    return (
      <div>
        <div className="text-xs font-semibold text-gray-500 mb-2 tracking-wide">
          Contracts
        </div>
        <BadgeList
          items={itemNames}
          count={2}
          badgeClassName="bg-gray-50 text-gray-700 border-gray-200"
          fallback={
            <span className="text-xs text-gray-400">Not specified</span>
          }
        />
      </div>
    );
  };

  const renderConfigItem = (label: string, value: string) => (
    <div>
      <div className="text-xs font-semibold text-gray-500 mb-2 tracking-wide">
        {label}
      </div>
      <div className="flex items-center min-h-[26px]">
        <span className="text-sm font-semibold text-gray-900">{value}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 mt-6">
      {/* Eligibility */}
      <Card className="shadow-sm border border-gray-200">
        <CardHeader className="pb-3 pt-5">
          <CardTitle className="text-sm font-semibold text-gray-900">
            Eligibility
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-6 pb-5">
          {renderEligibilityTags(
            planDetail?.overview?.eligibility?.categories,
            "Categories",
          )}
          {renderEligibilityTags(
            planDetail?.overview?.eligibility?.subCategories,
            "Sub-Categories",
          )}
          {renderContractTags(planDetail?.contract)}
          {renderConfigItem(
            "Billing Cycle",
            planDetail?.overview?.planConfiguration?.billingCycleDisplay || "-",
          )}
        </CardContent>
      </Card>

      {/* Utility Coverage */}
      <Card className="shadow-sm border border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between pb-3 pt-5">
          <CardTitle className="text-sm font-semibold text-gray-900">
            Utility Coverage
          </CardTitle>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-50 text-cyan-700 border border-cyan-200">
            {planDetail?.overview?.utilityCoverage?.length || 0} services
          </span>
        </CardHeader>
        <CardContent className="pb-5">
          {planDetail?.overview?.utilityCoverage?.length > 0 ? (
            <div
              className={`grid gap-4 ${
                planDetail.overview.utilityCoverage.length === 1
                  ? "grid-cols-1"
                  : planDetail.overview.utilityCoverage.length === 2
                    ? "grid-cols-1 md:grid-cols-2"
                    : planDetail.overview.utilityCoverage.length === 3
                      ? "grid-cols-1 md:grid-cols-3"
                      : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
              }`}
            >
              {planDetail.overview.utilityCoverage.map((coverage: any) => (
                <div
                  key={coverage.service}
                  className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-white shadow-2xs"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
                      <Box className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-gray-900">
                        {coverage.service}
                      </span>
                      <span className="text-xs text-gray-500">
                        {coverage.unit}
                      </span>
                    </div>
                  </div>
                  {coverage.included && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      Included
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-400">
              No utility coverage specified
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OverviewTab;
